import { Screen } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/checkout-screen';
import { LINKS } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/links';
import { useAuth } from '@fans-fund-me/shared-common-esm-authentication';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  loadStripe,
  type StripeExpressCheckoutElementClickEvent,
  type StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { useCallback, useEffect, useState } from 'react';
import { redirect, useNavigate } from 'react-router';
import { useImmer } from 'use-immer';
import { createSinglePayment, getSurpriseGiftPayment, updateSinglePayment } from '../../api/payments.ts';
import { getProfile } from '../../api/profile.ts';

function ElementsWrapper() {
  const [stripePromise] = useState(() => loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY!));

  const navigate = useNavigate();
  const auth = useAuth();

  const [clientSecret, setClientSecret] = useState('');

  const [hadResponse, setHadResponse] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [item, updateItem] = useImmer({
    image: '',
    name: '',
    price: '',
  });

  const [serviceFee, setServiceFee] = useState('');
  const [conversionFee, setConversionFee] = useState('');
  const [total, setTotal] = useState('');
  const [singlePaymentGiftId, setSinglePaymentGiftId] = useState('');

  useEffect(() => {
    const profileUserID = localStorage.getItem('ffm_checkout_user');
    const itemID = localStorage.getItem('ffm_checkout_item');
    const userCurrency = localStorage.getItem('ffm_currency');
    const isSurpriseGift = localStorage.getItem('ffm_checkout_surprise_gift');
    const surpriseGiftPaymentId = localStorage.getItem('ffm_checkout_surprise_gift_payment_id');
    const checkoutUsername = localStorage.getItem('ffm_checkout_username');

    if (profileUserID === null && itemID === null) {
      console.error('profileUserID and itemID null');
      if (checkoutUsername === null) {
        console.error('checkoutUsername also null');
        navigate(LINKS.HOME);
      } else {
        navigate(`/${checkoutUsername}`);
      }
    }

    if (!hadResponse) {
      if (auth.isLoggedIn && emailAddress === '') {
        getProfile(auth.id, auth.token).then((response) => {
          setEmailAddress(response.data.email);
        });
      }

      if (isSurpriseGift !== null && isSurpriseGift === 'true' && surpriseGiftPaymentId) {
        getSurpriseGiftPayment(surpriseGiftPaymentId, auth.token)
          .then((response) => {
            setHadResponse(true);
            setSinglePaymentGiftId(surpriseGiftPaymentId);
            updateItem((item) => {
              item.name = response.data.item.name;
              item.image = response.data.item.image;
              item.price = response.data.formattedSenderSubtotal;
            });
            setClientSecret(response.data.stripeSecret);
            setTotal(response.data.formattedSenderAmount);
            setServiceFee(response.data.formattedServiceCharge);
            setConversionFee(response.data.formattedConversionFee);
            // setCurrency(response.data.senderCurrency);
            setDisplayName(response.data.sellerName);
            setProfilePicture(response.data.sellerProfilePic);
          })
          .catch((error) => {
            console.error('Error getting surprise gift payment', JSON.stringify(error));
            if (error.response?.status === 403) {
              auth.logout();
              navigate(LINKS.LOGIN);
            } else if (checkoutUsername) {
              navigate(`/${checkoutUsername}`);
            }
          });
      } else {
        createSinglePayment(
          {
            receiverId: profileUserID,
            wishListItemId: itemID,
            currency: userCurrency,
          },
          auth.token,
        )
          .then((response) => {
            setHadResponse(true);
            setSinglePaymentGiftId(response.data.id);
            setClientSecret(response.data.stripeSecret);
            updateItem((item) => {
              item.name = response.data.item.name;
              item.image = response.data.item.image;
              item.price = response.data.formattedSenderSubtotal;
            });
            setTotal(response.data.formattedSenderAmount);
            setServiceFee(response.data.formattedServiceCharge);
            setConversionFee(response.data.formattedConversionFee);
            // setCurrency(response.data.senderCurrency);
            setDisplayName(response.data.sellerName);
            setProfilePicture(response.data.sellerProfilePic);
          })
          .catch((error) => {
            console.error('Error creating single payment', JSON.stringify(error));
            navigate(`/${checkoutUsername}`);
          });
      }
    }
  }, []);

  if (clientSecret === '') {
    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      <Screen
        item={{ image: '', name: '', price: '' }}
        isLoading={true}
        isLoadingPayment={true}
        expressCheckoutElement={<></>}
        paymentElement={<></>}
      />
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: clientSecret,
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css?family=Work+Sans',
          },
        ],
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#af8100',
            colorBackground: '#212121',
            spacingUnit: '0.25rem',
            fontFamily: 'Work Sans',
            borderRadius: '0.25rem',
            spacingAccordionItem: '1rem',
            spacingGridColumn: '2rem',
            spacingGridRow: '2rem',
          },
          rules: {
            '.AccordionItem': {
              boxShadow: 'none',
              border: 'none',
            },
            '.Input': {
              borderColor: '#5d5d5d',
              backgroundColor: '#2f3032',
              boxShadow: '0',
            },
            '.Block': {
              boxShadow: 'none',
              paddingTop: '1rem',
              paddingBottom: '1.5rem',
            },
          },
        },
      }}
    >
      <Checkout
        clientSecret={clientSecret}
        displayName={displayName}
        email={emailAddress}
        profilePicture={profilePicture!}
        item={item}
        serviceFee={serviceFee}
        conversionFee={conversionFee}
        total={total}
        singlePaymentGiftId={singlePaymentGiftId}
      />
    </Elements>
  );
}

function Checkout({
  clientSecret,
  displayName,
  email,
  profilePicture,
  item,
  serviceFee,
  conversionFee,
  total,
  singlePaymentGiftId,
}: {
  clientSecret: string;
  displayName: string;
  email: string;
  profilePicture: string;
  item: { image: string; name: string; price: string };
  serviceFee: string;
  conversionFee: string;
  total: string;
  singlePaymentGiftId: string;
}) {
  const auth = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [isLoadingPayment, setIsLoadingPayment] = useState(false);

  const [giftAnonymously, setGiftAnonymously] = useState(true);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [emailAddress, setEmailAddress] = useState(email);
  const [message, setMessage] = useState('');

  const [errorMessage, setErrorMessage] = useState('');
  const [emailErrorMessage, setEmailErrorMessage] = useState('');

  const handleSubmit = useCallback(
    async (event: React.SyntheticEvent | StripeExpressCheckoutElementConfirmEvent) => {
      // We don't want to let default form submission happen here,
      // which would refresh the page.
      if ('preventDefault' in event) {
        event.preventDefault();
      }

      if (!stripe || !elements) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      const { error } = await stripe.confirmPayment({
        redirect: 'always',
        clientSecret: clientSecret,
        //`Elements` instance that was used to create the Payment Element
        elements: elements,
        confirmParams: {
          return_url:
            import.meta.env.VITE_ENVIRONMENT === 'local'
              ? `http://localhost:5173/payment-complete/`
              : import.meta.env.VITE_ENVIRONMENT === 'dev'
                ? `https://dev.fansfund.me/payment-complete/`
                : `https://fansfund.me/payment-complete/`,
        },
      });
      if (error) {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Show error to your customer (for example, payment
        // details incomplete)
        console.error('Stripe checkout error:', JSON.stringify(error));
        setIsLoadingPayment(false);
        setErrorMessage(error.message!);

        if (error.payment_intent?.status === 'succeeded') {
          redirect(LINKS.PAYMENT_COMPLETE);
        }
      } else {
        // Your customer will be redirected to your `return_url`. For some payment
        // methods like iDEAL, your customer will be redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.
      }
    },
    [clientSecret, elements, stripe],
  );

  const ffmSubmit = useCallback(
    (event: React.SyntheticEvent | StripeExpressCheckoutElementConfirmEvent, passedEmail?: string | undefined) => {
      elements?.submit();
      // We don't want to let default form submission happen here,
      // which would refresh the page.
      if ('preventDefault' in event) {
        event.preventDefault();
      }

      setIsLoadingPayment(true);

      const email = passedEmail !== undefined ? passedEmail : emailAddress;

      if (email === '') {
        setEmailErrorMessage('Please enter your email address');
        setIsLoadingPayment(false);
        return;
      }

      if (!agreeTerms) {
        setErrorMessage('You must agree to our Terms and Conditions');
        setIsLoadingPayment(false);
        return;
      }

      updateSinglePayment(
        singlePaymentGiftId,
        {
          acceptedTermsAndConditions: agreeTerms,
          email: email,
          message: message,
          sendAnonymously: giftAnonymously,
        },
        auth.token,
      )
        .then(() => {
          setErrorMessage('');
          handleSubmit(event);
        })
        .catch((error) => {
          console.error('Error updating single payment:', JSON.stringify(error));
          setIsLoadingPayment(false);
          setEmailErrorMessage(
            error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'email',
            )?.message,
          );
          setErrorMessage(
            error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'agreeTerms',
            )?.message,
          );
        });
    },
    [elements, emailAddress, singlePaymentGiftId, agreeTerms, message, giftAnonymously, auth.token, handleSubmit],
  );

  useEffect(() => {
    if (emailErrorMessage !== '') {
      setEmailErrorMessage('');
    }
  }, [emailAddress]);

  useEffect(() => {
    if (errorMessage !== '') {
      setErrorMessage('');
    }
  }, [agreeTerms]);

  const handleConfirm = (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!agreeTerms) {
      return;
    }

    if (event.billingDetails?.email !== undefined) {
      setEmailAddress(event.billingDetails?.email);
      ffmSubmit(event, event.billingDetails?.email);
    } else {
      ffmSubmit(event);
    }
  };

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    if (!agreeTerms) {
      return;
    }

    const options = {
      emailRequired: true,
    };

    event.resolve(options);
  };

  return (
    <Screen
      displayName={displayName}
      profilePicture={profilePicture!}
      item={item}
      serviceFee={serviceFee}
      conversionFee={conversionFee}
      total={total}
      giftAnonymously={giftAnonymously}
      setGiftAnonymously={setGiftAnonymously}
      emailAddress={emailAddress}
      setEmailAddress={setEmailAddress}
      emailAddressError={emailErrorMessage}
      message={message}
      setMessage={setMessage}
      agreeTerms={agreeTerms}
      setAgreeTerms={setAgreeTerms}
      handleCheckout={ffmSubmit}
      isLoading={false}
      isLoadingPayment={isLoadingPayment}
      error={errorMessage}
      paymentElement={<PaymentElement />}
      expressCheckoutElement={<ExpressCheckoutElement onConfirm={handleConfirm} onClick={handleClick} />}
    />
  );
}

export default ElementsWrapper;
